import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import {
  addToCartApi,
  applyPromotionsApi,
  clearCartApi,
  getEligiblePromotionsApi,
  getPromoCodeApi,
  getStoreOrderApi,
  getTimeSlotsApi,
  prepareCheckoutApi,
  updateCollectionApi,
  updateCollectionTimeApi,
  updateDeliveryAddressTwoApi,
  updateDeliveryLocationApi,
  updatePickupLocationApi,
  updateStoreOrderItemQuantityApi
} from 'src/services/oms'
import { dateToRequestString } from 'src/utils/routes'
import { parsePromotion } from 'src/utils/promotion'
import type { MetaStateInReducer } from '../meta'
import type { OrderStateInReducer } from './slice'
import { isEmpty } from '../../utils/string'

export const fetchStoreOrderAction = createAsyncThunk(
  '@order/fetchStoreOrder',
  async (params, thunkApi) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const metaState = state['@meta']
    const {
      detailMenuSite: {
        data: { storeOrderId },
        mode
      }
    } = metaState
    if (mode === 'preview') throw Error('Cannot retrieve data on preview mode')
    const result = await getStoreOrderApi({ storeOrderId })
    return result.data
  }
)

export const getEligiblePromotionsAction = createAsyncThunk(
  '@order/getEligiblePromotions',
  async (params, thunkApi) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const metaState = state['@meta']
    const {
      detailMenuSite: {
        data: { storeOrderId },
        mode
      }
    } = metaState
    if (mode === 'preview') {
      throw Error('Cannot retrieve on preview mode')
    }
    const result = await getEligiblePromotionsApi({ storeOrderId })
    return result.data
  }
)

export const updateStoreOrderItemQuantityAction = createAsyncThunk(
  '@order/updateStoreOrderItemQuantity',
  async (
    { orderItemId, quantity }: { orderItemId: string; quantity: number },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer &
      OrderStateInReducer
    const metaState = state['@meta']
    const {
      detailMenuSite: {
        data: { storeOrderId }
      }
    } = metaState
    const orderState = state['@order']
    const {
      data: { orderItems }
    } = orderState.storeOrder
    const items = orderItems.map((item) =>
      item.id === orderItemId ? { ...item, quantity } : item
    )
    const result = await updateStoreOrderItemQuantityApi({
      storeOrderId,
      items
    })
    return result.data
  }
)

export const addItemToCartAction = createAsyncThunk(
  '@order/addItemToCart',
  async (
    {
      orderItem,
      menuItem,
      finalPrice
    }: {
      orderItem: {
        variantId: string
        quantity: number
        subitems: Record<string, unknown>[]
      }
      menuItem: Menu.MenuItem
      finalPrice: number
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const metaState = state['@meta']
    const {
      detailMenuSite: {
        data: { storeOrderId }
      }
    } = metaState
    const result = await addToCartApi({
      orderItem,
      storeOrderId
    })
    result.data.payload.menuItem = menuItem
    result.data.payload.finalPrice = finalPrice
    result.data.payload.orderItem = orderItem
    return result.data
  }
)

export const clearCartAction = createAsyncThunk(
  '@order/clearCart',
  async (params, thunkApi) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const metaState = state['@meta']
    const {
      detailMenuSite: {
        data: { storeOrderId }
      }
    } = metaState
    const result = await clearCartApi({ storeOrderId })
    return result.data
  }
)

export const fetchTimeSlotsAction = createAsyncThunk(
  '@order/fetchTimeSlots',
  async ({ date }: { date: string | Date }, thunkApi) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const metaState = state['@meta']
    const {
      detailMenuSite: {
        data: { storeOrderId }
      }
    } = metaState
    let dateValue = date
    if (typeof date === 'string') {
      dateValue = isEmpty(date) ? new Date() : date
    } 
    const result = await getTimeSlotsApi({ storeOrderId, date: dateValue })
    return result.data
  }
)

export const syncMethodAction = createAsyncThunk(
  '@order/syncMethod',
  async ({ method }: { method: string }, thunkApi) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const metaState = state['@meta']
    const {
      detailMenuSite: {
        data: { storeOrderId }
      }
    } = metaState
    const result = await updateCollectionApi({ storeOrderId, method })
    return result.data
  }
)

export const syncPickupLocationAction = createAsyncThunk(
  '@order/syncPickupLocation',
  async (
    {
      storeId,
      lat,
      long,
      selectedDate
    }: {
      storeId: string
      lat?: number | string
      long?: number | string
      selectedDate?: string
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const metaState = state['@meta']
    const {
      detailMenuSite: {
        data: { storeOrderId }
      }
    } = metaState
    let params
    if (lat && long && selectedDate) {
      params = {
        lat: lat.toString(),
        long: long.toString(),
        selectedDate: dateToRequestString(selectedDate),
        storeOrderId
      }
    } else {
      params = { storeOrderId, storeId }
    }
    const result = await updatePickupLocationApi(params)
    return result.data
  }
)

export const syncDeliveryLocationAction = createAsyncThunk(
  '@order/syncDeliveryLocation',
  async (
    {
      address
    }: {
      address: {
        selectedAddressId?: string
        deliveryAddress: string
        deliveryAddressOne: string
        deliveryAddressTwo: string
        deliveryAddressCity?: string
        deliveryAddressState?: string
        deliveryAddressCountry: string
        deliveryAddressPostal: string
        latitude: number | string
        longitude: number | string
        addressTwoDetails?: Record<string, string>
      }
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const metaState = state['@meta']
    const {
      detailMenuSite: {
        data: { storeOrderId }
      }
    } = metaState
    const result = await updateDeliveryLocationApi({ storeOrderId, address })
    return result.data
  }
)

export const syncDeliveryAddressTwoAction = createAsyncThunk(
  '@order/syncDeliveryAddressTwo',
  async (
    {
      addressTwo
    }: {
      addressTwo: string
    },
    thunkApi
  ) => {
    try {
      const state = thunkApi.getState() as MetaStateInReducer
      const metaState = state['@meta']
      const {
        detailMenuSite: {
          data: { storeOrderId }
        }
      } = metaState
      const result = await updateDeliveryAddressTwoApi({
        storeOrderId,
        addressTwo
      })
      return result.data
    } catch (err) {
      return thunkApi.rejectWithValue(err.response.data)
    }
  }
)

export const syncAppointmentTimeAction = createAsyncThunk(
  '@order/syncAppointmentTime',
  async (
    {
      collectionDate,
      collectionTimeSlot,
      collectionTimeSlotId
    }: {
      collectionDate: string
      collectionTimeSlot: string
      collectionTimeSlotId: string
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const metaState = state['@meta']
    const {
      detailMenuSite: {
        data: { storeOrderId }
      }
    } = metaState
    const result = await updateCollectionTimeApi({
      storeOrderId,
      collectionDate,
      collectionTimeSlot,
      collectionTimeSlotId
    })
    return result.data
  }
)

export const applyPromotionsAction = createAsyncThunk(
  '@order/applyPromotions',
  async (
    {
      promotions
    }: {
      promotions: {
        id: string
        selectedItems?: {
          id: string
          itemReference: string
          quantity: number
        }[]
      }[]
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const metaState = state['@meta']
    const {
      detailMenuSite: {
        data: { storeOrderId }
      }
    } = metaState
    const result = await applyPromotionsApi({
      storeOrderId,
      promotions: promotions.map(parsePromotion)
    })
    return result.data
  }
)

export const checkPromoCodeAction = createAsyncThunk(
  '@order/checkPromoCode',
  async (
    {
      code
    }: {
      code: string
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const metaState = state['@meta']
    const {
      detailMenuSite: {
        data: { storeOrderId }
      }
    } = metaState
    const result = await getPromoCodeApi({
      storeOrderId,
      code
    })
    return result.data
  }
)

export const togglePromotionAction = createAction(
  '@order/togglePromotion',
  ({
    promotion
  }: {
    promotion: SelectedPromotion
  }): {
    type: string
    payload: {
      promotion: SelectedPromotion
    }
  } => {
    return {
      type: '@order/togglePromotion',
      payload: { promotion }
    }
  }
)

export const prepareCheckoutAction = createAsyncThunk(
  '@order/prepareCheckout',
  async (params, thunkApi) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const metaState = state['@meta']
    const {
      detailMenuSite: {
        data: { storeOrderId }
      }
    } = metaState
    const result = await prepareCheckoutApi({ storeOrderId })
    return result.data
  }
)

export const resetAppointmentAction = createAction('@order/resetAppointment')
